import { useQuery } from '@apollo/client/react';
import clsx from 'clsx';
import { camelCase, capitalize, isEqual } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CityWithJobCount, JobBoardDto } from '@pflegia/data-access';
import { CandidateDocument } from '@pflegia/data-access/candidate.generated';
import { CandidateFilterType } from '@pflegia/types/api/candidate/candidate-filter-type';
import { TypeOfWork } from '@pflegia/types/api/candidate/type-of-work';
import { FilterValue } from '@pflegia/types/api/job-board/filter-value';
import { REVERSED_JOB_TITLES_TRANSLATIONS } from '@pflegia/types/api/job-title/job-titles.enum';
import { ITEMS_PER_PAGE } from '@pflegia/types/common/job-board/constants';
import { LocationObject } from '@pflegia/types/common/location/location-object';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { HOME, JOB_BOARD, JOBS } from '@pflegia/ui/pflegia/constants/routes';
import ContainerSection from '@pflegia/ui/shared/container-section';
import JobBoardWrapper from '@pflegia/ui/shared/job-board/JobBoardWrapper';
import { LoadingCardsContainer } from '@pflegia/ui/shared/job-board/LoadingCardsContainer';
import useJobTitle from '@pflegia/ui/shared/job-title/hooks/use-job-title';
import { getLocationValue } from '@pflegia/util/common/location';
import { normalizeJobsParams } from '@pflegia/util/common/normalize-jobs-params';
import { stripFlavor } from '@pflegia/util/common/path-utils';
import { isDefined } from '@pflegia/util/common/type-guards';

import WaOptInFifthJobSentinel from '@domains/whatsapp-opt-in/WaOptInFifthJobSentinel';

import NextSeo from '@shared/components/NextSeo';
import Breadcrumb from '@shared/components/breadcrumb/Breadcrumb';
import { LoggedOutFilterType } from '@shared/enums/logged-out-filter-type.enum';
import { useRandomizedFaqModel } from '@shared/hooks/use-randomized-faq-model';
import { useRouterHelper } from '@shared/hooks/use-router-helper';
import { getBaseUrlByCurrentSourceDomain } from '@shared/utils/domain';
import { loadNamespaces } from '@shared/utils/load-namespaces';

import { useJobListState } from './application/use-job-list-state.hook';
import useJobTitlesFilterList from './application/use-job-titles-filter-list.hook';
import JobBoardHeading, { getDistanceLimit } from './components/JobBoardHeading';
import useJobBoardSeoContent from './components/job-board-seo-content/use-job-board-seo-content';
import { JobTitlePageBackLink } from './components/job-title-page/JobTitlePageBackLink';
import { JobTitlePageMagazineSection } from './components/job-title-page/JobTitlePageMagazineSection';
import { JobTitlePageRichContent } from './components/job-title-page/JobTitlePageRichContent';
import { MarketStatsGeoText } from './components/market-stats/MarketStatsGeoText';
import { MarketStatsStrip } from './components/market-stats/MarketStatsStrip';
import NavigationCluster from './components/navigation-cluster/NavigationCluster';
import WrappedSalaryEstimationSection from './components/salary-estimation';
import { FilterSortLineSkeleton } from './components/skeletons/FilterSortLineSkeleton/FilterSortLineSkeleton';
import { QUESTIONS_AND_ANSWERS } from './constants/faq';
import { FiltersDto } from './dto/filters-dto';
import { MAX_LLP_FAQ_EMPLOYERS, useDynamicLlpFaqs } from './hooks/use-dynamic-llp-faqs';
import { useFaq } from './hooks/use-faq';
import useInitializeFilters from './hooks/use-initialize-filters';
import { useJobBoardSeoMeta } from './hooks/use-job-board-seo-meta';
import useJobBoardSeoObjects from './hooks/use-job-board-seo-objects';
import {
  buildSyncedJobBoardRoutePath,
  normalizeSyncedJobBoardPath,
  replaceSyncedJobBoardRoute,
  useSyncJobBoardRoute,
} from './hooks/use-sync-job-board-route';
import JobBoardPageProps from './types/job-board-page';
import { buildJobListingFaqs } from './utils/build-job-listing-faqs';
import { buildJobListingSchemaUrls } from './utils/build-job-listing-schema-urls';
import { buildPrimaryJobBoardSeoState } from './utils/build-primary-job-board-seo-state';
import { getActiveJobBoardSeoMeta } from './utils/get-active-job-board-seo-meta';
import { getJobsSeoPageType } from './utils/get-jobs-seo-page-type';
import { isOnlyJobTitleFilter } from './utils/is-only-job-title-filter';
import { isPflegefachkraftRoleGroupActive, isPflegefachkraftRoleGroupSelected } from './utils/pflegefachkraft-role-group';

const JobPositionListLoading: React.FC = () => {
  const { isTransparentJobBoard } = useRouterHelper();
  return (
    <div className='mt-6 flex h-full flex-col gap-6 lg:mt-8'>
      <FilterSortLineSkeleton />
      <LoadingCardsContainer slimmed={isTransparentJobBoard} />
    </div>
  );
};

const JobBoardSeoContent = dynamic(() => import('./components/job-board-seo-content/JobBoardSeoContent'), { ssr: false });
const JobPositionList = dynamic(() => import('./components/job-position-list'), {
  ssr: false,
  loading: () => <JobPositionListLoading />,
});
const JobPositionListJsonLd = dynamic(() => import('./components/JobPositionListJsonLd'));
const JobPausedWithSuggestions = dynamic(() => import('@domains/job-detail/components/job-paused/JobPausedWithSuggestions.component'), {
  ssr: false,
});
const FilterSidebar = dynamic(() => import('./components/filter-side-bar/FilterSidebar.component').then(mod => mod.FilterSidebar), {
  ssr: false,
});
const FilterSection = dynamic(() => import('./components/filter-section/FilterSection.component'), {
  loading: () => <div className='h-57.5 bg-white md:h-36' />,
  ssr: false,
});
const MagazinSliderSection = dynamic(() => import('@domains/magazin/components/magazin-slider-section/MagazinSliderSection'), {
  ssr: false,
  loading: () => <div className='min-h-125' />,
});
const PositionCluster = dynamic(() => import('../landing-page/components/position-cluster/PositionCluster'));
const QuestionsAndAnswers = dynamic(() => import('@pflegia/ui/shared/landing-page/questions-and-answers/QuestionsAndAnswers'));

export interface JobListProps {
  jobTitle?: JobBoardPageProps['jobTitle'];
  pflegefachkraftRoleGroup?: JobBoardPageProps['pflegefachkraftRoleGroup'];
  location?: LocationObject;
  typeOfWork?: TypeOfWork;
  slimmed?: boolean;
  canBrowseWithoutFilters?: boolean;
  children?: React.ReactNode;
  namespace?: 'jobBoard' | 'jobListings';
  nearbyCities?: CityWithJobCount[];
  initialTotal?: number;
  jobTitlePage?: JobBoardPageProps['jobTitlePage'];
  seoMeta?: JobBoardPageProps['seoMeta'];
}

// Updated TitleSkeleton component
const TitleSkeleton: React.FC = () => (
  <div className='mt-12 flex flex-col space-y-2'>
    <div className='h-9 w-full animate-pulse rounded-sm bg-gray-200 md:w-5/6' />
    <div className='h-9 w-5/6 animate-pulse rounded-sm bg-gray-200 md:hidden' />
    <div className='h-9 w-2/3 animate-pulse rounded-sm bg-gray-200 md:hidden' />
  </div>
);

const JobList: React.FC<JobListProps> = ({
  jobTitle,
  pflegefachkraftRoleGroup,
  location,
  typeOfWork,
  slimmed,
  canBrowseWithoutFilters = slimmed,
  namespace = 'jobBoard',
  nearbyCities,
  initialTotal,
  jobTitlePage,
  seoMeta,
}) => {
  const router = useRouter();
  const isAuthenticated = useAuth();
  const [pendingRouteDrivenQueryPath, setPendingRouteDrivenQueryPath] = useState<string | null>(null);
  const strippedRouterAsPath = useMemo(() => stripFlavor(router.asPath), [router.asPath]);
  useInitializeFilters({
    jobTitleId: jobTitle?.id,
    jobTitleIds: pflegefachkraftRoleGroup?.jobTitleIds,
    isJobTitleResolved: Boolean(jobTitle?.id),
    location,
    typeOfWork,
    isAuthenticated,
    isUrlFilterSyncSkipped: Boolean(pendingRouteDrivenQueryPath),
  });

  const [isFiltersSidebarOpen, setIsFiltersSidebarOpen] = useState(false);

  useEffect(() => {
    loadNamespaces(['funnel', 'jobDetails', 'magazin', 'modalPending', 'profile', 'user']);
  }, []);

  const {
    isLoading,
    jobPositionListQuery,
    page,
    setPage,
    filterState,
    applyFilters: applyFiltersBase,
    queryVariableFilters,
    isStateOnlySearch,
    isCityStateSearch,
  } = useJobListState(canBrowseWithoutFilters, ITEMS_PER_PAGE, {
    isJobPositionListQuerySkippedExternally: Boolean(pendingRouteDrivenQueryPath),
    routeLocation: location,
  });
  const { data, loading: jobPositionListLoading, error: jobPositionListError } = jobPositionListQuery;
  const isJobListingsPage = namespace === 'jobListings';
  const { t: tJobListings } = useTranslation('jobListings');
  const { t: tJobBoard } = useTranslation('jobBoard');
  const { t: tCandidateFields } = useTranslation('candidateFields');
  const { shouldShowFaq, faqVariant, relatedJobTitles, relatedLocations, jobPositionFilterValue, locationFilterValue, hasMultipleFilters } =
    useFaq({
      filterState,
      jobTitle,
      location,
      nearbyCities,
      pflegefachkraftRoleGroup,
    });
  const { jobTitleIds, locations } = queryVariableFilters || {};
  const { jobTitles, isLoading: isJobTitlesFilterLoading } = useJobTitlesFilterList();
  const filtersDto = useMemo(() => FiltersDto.create(filterState.filters), [filterState.filters]);
  const jobTitlesForMagazin = useMemo(
    () =>
      (jobTitleIds || []).flatMap(id => {
        const text = jobTitles.find(jt => jt.value === id)?.text;
        const enumKey = text ? REVERSED_JOB_TITLES_TRANSLATIONS[text] : undefined;
        return enumKey ? { id: enumKey, title: enumKey } : [];
      }),
    [jobTitleIds, jobTitles],
  );

  const { data: candidateData } = useQuery(CandidateDocument, { skip: !isAuthenticated });
  const isPending = Boolean(candidateData?.candidate.jobTitleAnswers?.some(jobTitleAnswer => jobTitleAnswer.isPending));
  const { translateJobTitle } = useJobTitle();
  const { sourceDomain, sourceDomainName, isPraxia } = useFlavorFromRoute();
  const { isMinifiedJobBoard, isTransparentJobBoard, isJobBoard } = useRouterHelper();
  const jobListingsBaseUrl = isTransparentJobBoard ? JOBS : JOB_BOARD;
  const shouldUseRouteSeoFallbacks = !filterState.filtersFullyInitialized;
  const routeJobTitleForSeo = useMemo(() => (jobTitle ? translateJobTitle(jobTitle) : ''), [jobTitle, translateJobTitle]);
  const routeLocationForSeo = useMemo(() => getLocationValue(location), [location]);
  const buildPrimarySeoStateForFilters = useCallback(
    (filters: FilterValue[]) => {
      const nextFiltersDto = FiltersDto.create(filters);
      const nextPositionFilters = nextFiltersDto.getPositionFilters();
      const [nextPrimaryPositionFilter] = nextPositionFilters;
      const shouldUseRouteJobTitleFallbackForFilters = shouldUseRouteSeoFallbacks || nextPrimaryPositionFilter?.value === jobTitle?.id;
      const shouldUsePflegefachkraftRoleGroup = isPflegefachkraftRoleGroupActive(
        nextPositionFilters.map(({ value }) => value),
        pflegefachkraftRoleGroup,
        shouldUseRouteSeoFallbacks,
      );

      return buildPrimaryJobBoardSeoState({
        basePath: jobListingsBaseUrl,
        defaultJobTitle: shouldUseRouteJobTitleFallbackForFilters ? jobTitle : undefined,
        defaultJobTitleForSeo: shouldUseRouteJobTitleFallbackForFilters ? routeJobTitleForSeo : '',
        defaultLocation: shouldUseRouteSeoFallbacks ? location : undefined,
        defaultLocationForSeo: shouldUseRouteSeoFallbacks ? routeLocationForSeo : '',
        defaultTypeOfWork: shouldUseRouteSeoFallbacks ? typeOfWork : undefined,
        filtersDto: nextFiltersDto,
        jobTitles,
        pflegefachkraftRoleGroup: shouldUsePflegefachkraftRoleGroup ? pflegefachkraftRoleGroup : undefined,
        translateTypeOfWork: value => tCandidateFields(`typesOfWork.${camelCase(value)}`),
      });
    },
    [
      jobListingsBaseUrl,
      jobTitle,
      jobTitles,
      location,
      pflegefachkraftRoleGroup,
      routeJobTitleForSeo,
      routeLocationForSeo,
      shouldUseRouteSeoFallbacks,
      tCandidateFields,
      typeOfWork,
    ],
  );
  const {
    canSyncRoute,
    jobTitle: primaryJobTitle,
    jobTitleForSeo: primaryJobTitleForSeo,
    listingPath: primaryListingPath,
    locationForSeo: primaryLocationForSeo,
    locationObject: primaryLocationObject,
    typeOfWork: primaryTypeOfWork,
  } = useMemo(() => buildPrimarySeoStateForFilters(filterState.filters), [buildPrimarySeoStateForFilters, filterState.filters]);

  const isRouteReflectedInFilters = useMemo(
    () =>
      isAuthenticated ||
      ((!jobTitle || filtersDto.getPositionFilters()[0]?.value === jobTitle.id) &&
        (!pflegefachkraftRoleGroup ||
          isPflegefachkraftRoleGroupSelected(
            filtersDto.getPositionFilters().map(({ value }) => value),
            pflegefachkraftRoleGroup,
          )) &&
        (!location || filtersDto.getLocationFilters()[0]?.value === location.formattedAddress) &&
        (!typeOfWork || filtersDto.getFacilityFilters()[0]?.value === typeOfWork)),
    [isAuthenticated, jobTitle, pflegefachkraftRoleGroup, location, typeOfWork, filtersDto],
  );

  const { isRouteSyncPending } = useSyncJobBoardRoute({
    canonicalPath: primaryListingPath,
    isEnabled: filterState.filtersFullyInitialized && canSyncRoute && isRouteReflectedInFilters,
    requestedSyncedRoutePath: pendingRouteDrivenQueryPath,
    shouldResetPageOnSync: true,
  });

  const applyFilters = useCallback(
    async (filters: FilterValue[]) => {
      if (isLoading || isEqual(filters, filterState.filters)) {
        return;
      }

      const nextListingPath = buildPrimarySeoStateForFilters(filters).listingPath;
      const nextSyncedRoutePath =
        nextListingPath === primaryListingPath
          ? null
          : buildSyncedJobBoardRoutePath({
              canonicalPath: nextListingPath,
              currentPath: strippedRouterAsPath,
              shouldResetPage: true,
            });
      const shouldSyncRouteNow = Boolean(nextSyncedRoutePath) && normalizeSyncedJobBoardPath(strippedRouterAsPath) !== nextSyncedRoutePath;

      if (shouldSyncRouteNow && nextSyncedRoutePath) {
        setPendingRouteDrivenQueryPath(nextSyncedRoutePath);
      }
      // Trigger the shallow replace from the interaction itself so first-filter changes update
      // the URL immediately instead of waiting for a follow-up render cycle.
      const routeSyncPromise =
        shouldSyncRouteNow && nextSyncedRoutePath ? replaceSyncedJobBoardRoute({ router, syncedRoutePath: nextSyncedRoutePath }) : null;

      try {
        await applyFiltersBase(filters, {
          isPageResetHandledExternally: Boolean(routeSyncPromise),
        });
        await routeSyncPromise;
      } finally {
        if (shouldSyncRouteNow && nextSyncedRoutePath) {
          setPendingRouteDrivenQueryPath(currentPath => (currentPath === nextSyncedRoutePath ? null : currentPath));
        }
      }
    },
    [applyFiltersBase, buildPrimarySeoStateForFilters, filterState.filters, isLoading, primaryListingPath, router, strippedRouterAsPath],
  );

  const { seoMeta: currentSeoMeta, isLoading: isSeoMetaLoading } = useJobBoardSeoMeta({
    initialSeoMeta: seoMeta,
    pageType: getJobsSeoPageType({
      hasJobTitle: Boolean(primaryJobTitleForSeo),
      hasLocation: Boolean(primaryLocationForSeo),
      typeOfWork: primaryTypeOfWork,
    }),
    listingPath: primaryListingPath,
    isDisabled: hasMultipleFilters,
  });

  const activeSeoMeta = useMemo(
    () =>
      getActiveJobBoardSeoMeta({
        seoMeta: currentSeoMeta,
        listingPath: primaryListingPath,
        hasMultipleFilters,
        values: {
          role: primaryJobTitleForSeo,
          city: primaryLocationForSeo,
          state: primaryLocationObject?.administrativeAreaLevel1 ?? primaryLocationObject?.state,
          count: data?.data?.total ?? initialTotal,
          radius: getDistanceLimit(
            isTransparentJobBoard,
            Boolean(data?.data?.extendedJobPositions?.length),
            data?.data?.expandedRadiusKm ?? undefined,
          ),
          domain: sourceDomainName,
        },
      }),
    [
      currentSeoMeta,
      primaryListingPath,
      hasMultipleFilters,
      primaryJobTitleForSeo,
      primaryLocationForSeo,
      primaryLocationObject?.administrativeAreaLevel1,
      primaryLocationObject?.state,
      data?.data?.total,
      data?.data?.extendedJobPositions?.length,
      data?.data?.expandedRadiusKm,
      initialTotal,
      isTransparentJobBoard,
      sourceDomainName,
    ],
  );

  const { seoObjects, shouldRenderSeoWithFilters } = useJobBoardSeoObjects({
    location: primaryLocationForSeo,
    jobTitleForSeo: primaryJobTitleForSeo,
    jobTitle: primaryJobTitle,
    path: primaryListingPath,
    canBrowseWithoutFilters,
    seoOverrides: activeSeoMeta,
  });

  const jobTitleFilterValue = filterState.filters?.find(({ type }) =>
    [LoggedOutFilterType.Position, CandidateFilterType.Position].includes(type),
  );
  const selectedJobTitle = jobTitles.find(({ value }) => value === jobTitleFilterValue?.value)?.text ?? '';
  const seoContentProps = useJobBoardSeoContent(
    jobTitleIds,
    locations,
    shouldUseRouteSeoFallbacks ? jobTitle : undefined,
    shouldUseRouteSeoFallbacks ? location : undefined,
  );

  const query = new URLSearchParams();
  const locationForSeo = useMemo(
    () =>
      primaryLocationObject?.city ||
      primaryLocationObject?.administrativeAreaLevel1 ||
      primaryLocationObject?.state ||
      seoContentProps?.city ||
      locations?.[0]?.city ||
      locations?.[0]?.administrativeAreaLevel1 ||
      locations?.[0]?.state ||
      '',
    [
      locations,
      primaryLocationObject?.administrativeAreaLevel1,
      primaryLocationObject?.city,
      primaryLocationObject?.state,
      seoContentProps?.city,
    ],
  );
  if (locationForSeo) query.set('standort', locationForSeo.toLowerCase());
  if (primaryJobTitleForSeo) query.set('position', primaryJobTitleForSeo.toLowerCase());
  const currentBreadCrumbUrl = query.toString() ? `${jobListingsBaseUrl}?${query.toString()}` : `${jobListingsBaseUrl}`;

  const breadcrumbItems = useMemo(
    () => [
      {
        label: 'Startseite',
        href: HOME,
      },
      {
        label: locationForSeo ? `Stellenangebote in ${capitalize(locationForSeo)}` : 'Stellenangebote',
        href: jobListingsBaseUrl + (locationForSeo ? `?standort=${locationForSeo.toLowerCase()}` : ''),
      },
      ...(primaryJobTitleForSeo
        ? [
            {
              label: locationForSeo ? `${primaryJobTitleForSeo} Jobs in ${capitalize(locationForSeo)}` : `${primaryJobTitleForSeo} Jobs`,
              href: currentBreadCrumbUrl,
            },
          ]
        : []),
    ],
    [currentBreadCrumbUrl, locationForSeo, jobListingsBaseUrl, primaryJobTitleForSeo],
  );
  const shouldAddBreacrumbJsonIdSchema = useMemo(
    () => !isJobBoard && !jobPositionListLoading && (data?.data.total ?? 0) > 0,
    [isJobBoard, jobPositionListLoading, data?.data.total],
  );

  const selectModel = useRandomizedFaqModel(jobTitle?.id || jobTitleFilterValue?.value || '');
  const translationParameters = useMemo(
    () => ({
      domain: sourceDomainName,
      translatedJobTitle: jobPositionFilterValue,
      location: locationFilterValue,
    }),
    [sourceDomainName, jobPositionFilterValue, locationFilterValue],
  );

  const displayTotal = data?.data?.total ?? initialTotal;
  const employerNames = useMemo(() => {
    const names =
      (data as { data?: JobBoardDto })?.data?.jobPositions?.map(item => item.jobPosition?.employer?.name).filter(isDefined) ?? [];
    return Array.from(new Set(names)).slice(0, MAX_LLP_FAQ_EMPLOYERS);
  }, [data]);
  const dynamicFaqs = useDynamicLlpFaqs({
    employerNames,
    faqVariant,
    jobTitleId: jobTitleIds?.[0],
    location: locations?.[0],
    locationLabel: locationFilterValue,
    tJobListings,
    translatedJobTitle: jobPositionFilterValue,
    vacancyCount: displayTotal,
  });

  const legacyFaqs = useMemo(() => {
    if (namespace === 'jobBoard') return QUESTIONS_AND_ANSWERS;
    if (faqVariant === 'none') return [];

    return buildJobListingFaqs({
      faqVariant,
      relatedLocations,
      selectModel,
      tJobListings,
      translatedJobTitle: jobPositionFilterValue,
      translationParameters,
    });
  }, [namespace, faqVariant, relatedLocations, tJobListings, translationParameters, selectModel, jobPositionFilterValue]);
  const faqs = dynamicFaqs ?? legacyFaqs;
  const shouldRenderTransparentClusters = !isPraxia && isTransparentJobBoard;
  const shouldRenderPositionCluster = shouldRenderTransparentClusters && !jobPositionFilterValue;
  const shouldRenderFaqSection = faqs.length > 0 && (isJobListingsPage ? shouldShowFaq : faqVariant === 'jobAndLocation');
  const jobsBaseUrl = getBaseUrlByCurrentSourceDomain(sourceDomain);
  const processedSchemaUrls = useMemo(
    () =>
      buildJobListingSchemaUrls({
        jobsBaseUrl,
        relatedJobTitles,
        locationFilterValue,
        relatedLocations,
        jobPositionFilterValue,
      }),
    [jobsBaseUrl, relatedJobTitles, locationFilterValue, relatedLocations, jobPositionFilterValue],
  );

  // Route props are the source on SSR, after hydration the job title content only stays while the filters still match the route.
  const [primaryRoutePositionFilter] = filtersDto.getPositionFilters();
  const isRouteJobTitleShown = shouldUseRouteSeoFallbacks || (Boolean(jobTitle) && primaryRoutePositionFilter?.value === jobTitle?.id);
  const isRouteJobTitleOnlyFilter = shouldUseRouteSeoFallbacks || isOnlyJobTitleFilter(filterState.filters, jobTitle?.id);
  const isNationalJobTitlePage = isTransparentJobBoard && Boolean(jobTitle) && !location && !typeOfWork && isRouteJobTitleOnlyFilter;
  const isJobTitleCityPage = isTransparentJobBoard && Boolean(jobTitle) && Boolean(location?.city) && isRouteJobTitleShown;
  const jobTitleSlug = normalizeJobsParams(routeJobTitleForSeo);
  const jobTitleCityLinks = isNationalJobTitlePage
    ? { cities: jobTitlePage?.cities ?? [], jobTitle: routeJobTitleForSeo, jobTitleSlug }
    : undefined;

  const showTitleSkeleton =
    (!isDefined(displayTotal) && jobPositionListLoading && !jobPositionListError) || isRouteSyncPending || isSeoMetaLoading;

  const headingKey = useMemo(() => {
    if (hasMultipleFilters) return isPraxia ? 'headingMultipleFiltersPraxia' : 'headingMultipleFilters';
    // A state-only location has no meaningful radius, so it shares the wording used when there's no location at all.
    if (jobPositionFilterValue && locationFilterValue) return isStateOnlySearch ? 'headingWithoutRadius' : 'heading';
    if (jobPositionFilterValue) return 'headingWithoutRadius';
    if (locationFilterValue) return 'headingWithoutJobTitle';
    return 'headingWithoutJobTitleAndLocation';
  }, [jobPositionFilterValue, locationFilterValue, hasMultipleFilters, isPraxia, isStateOnlySearch]);

  const faqHeadingKey = useMemo(() => {
    if (jobPositionFilterValue && locationFilterValue) return 'questionsAndAnswers.title';
    if (jobPositionFilterValue) return 'questionsAndAnswers.titleWithoutLocation';
    return 'questionsAndAnswers.titleWithoutJobTitle';
  }, [jobPositionFilterValue, locationFilterValue]);

  return (
    <>
      <NextSeo {...seoObjects} />
      {shouldAddBreacrumbJsonIdSchema && <Breadcrumb items={breadcrumbItems} showBreadcrumb={false} />}
      {isTransparentJobBoard && <JobPositionListJsonLd city={locationForSeo} jobs={(data as { data: JobBoardDto })?.data.jobPositions} />}
      <JobBoardWrapper isFiltersSidebarOpen={isFiltersSidebarOpen}>
        <ContainerSection className={clsx('mt-6 px-0', isFiltersSidebarOpen && 'hidden lg:block', slimmed && 'mb-6')}>
          <FilterSection
            filterState={filterState}
            isPending={isPending}
            applyFilters={applyFilters}
            primaryBorderForMissingFilters={canBrowseWithoutFilters}
          />
        </ContainerSection>
        <div className='bg-background-secondary grow'>
          {!isMinifiedJobBoard && isFiltersSidebarOpen && (
            <FilterSidebar
              setOpen={setIsFiltersSidebarOpen}
              filterState={filterState}
              applyFilters={applyFilters}
              // @ts-expect-error Not every property from relations is required
              candidateProfile={candidateData?.candidate}
              isPending={isPending}
            />
          )}

          {isMinifiedJobBoard && !filtersDto.hasMissingRequiredFilters() ? <JobPausedWithSuggestions /> : null}
          <div className={clsx({ 'hidden md:block': isFiltersSidebarOpen }, 'mb-6')}>
            <ContainerSection>
              {!isMinifiedJobBoard && shouldRenderSeoWithFilters && (
                <>
                  {showTitleSkeleton ? (
                    <TitleSkeleton />
                  ) : (
                    <JobBoardHeading
                      hasExtendedPositions={Boolean(data?.data?.extendedJobPositions?.length)}
                      heading={activeSeoMeta?.h1}
                      headingKey={headingKey}
                      isTransparentJobBoard={isTransparentJobBoard}
                      jobTitle={jobPositionFilterValue}
                      location={locationFilterValue}
                      numberOfJobs={displayTotal}
                      expandedRadiusKm={data?.data?.expandedRadiusKm ?? undefined}
                    />
                  )}
                </>
              )}

              {isNationalJobTitlePage && jobTitlePage?.intro && (
                <p className='mt-2 max-w-4xl text-sm text-gray-700 lg:text-base' data-testid='job-title-page-intro'>
                  {jobTitlePage.intro}
                </p>
              )}

              {!isMinifiedJobBoard && isTransparentJobBoard && jobPositionFilterValue && locationFilterValue && (
                <MarketStatsStrip jobTitleIds={jobTitleIds} locations={locations} totalJobs={displayTotal} />
              )}

              <JobPositionList
                filtersDto={filtersDto}
                isCityStateSearch={isCityStateSearch}
                isLoading={isLoading || isRouteSyncPending}
                isStateOnlySearch={isStateOnlySearch}
                jobPositionListQuery={jobPositionListQuery}
                page={page}
                setOpen={setIsFiltersSidebarOpen}
                setPage={setPage}
                footer={
                  isMinifiedJobBoard ? null : (
                    <WrappedSalaryEstimationSection jobTitle={selectedJobTitle} jobTitleIds={jobTitleIds} locations={locations} />
                  )
                }
                browsingTriggerSlot={<WaOptInFifthJobSentinel />}
                midListSlot={
                  !isMinifiedJobBoard &&
                  isTransparentJobBoard &&
                  jobPositionFilterValue &&
                  locationFilterValue && (
                    <MarketStatsGeoText
                      jobTitle={selectedJobTitle}
                      jobTitleIds={jobTitleIds}
                      locations={locations}
                      totalJobs={displayTotal}
                    />
                  )
                }
                slimmed={slimmed}
                canBrowseWithoutFilters={canBrowseWithoutFilters}
              />
            </ContainerSection>
          </div>
        </div>
        {seoContentProps && <JobBoardSeoContent {...seoContentProps} />}

        {isNationalJobTitlePage && jobTitlePage?.content && <JobTitlePageRichContent html={jobTitlePage.content} />}

        {isJobTitleCityPage && <JobTitlePageBackLink jobTitle={routeJobTitleForSeo} jobTitleSlug={jobTitleSlug} />}

        {shouldRenderTransparentClusters && (
          <NavigationCluster
            location={locationFilterValue}
            nearbyCities={nearbyCities}
            hasPosition={Boolean(jobPositionFilterValue)}
            jobTitleCityLinks={jobTitleCityLinks}
          />
        )}

        {shouldRenderPositionCluster && (
          <ContainerSection>
            <PositionCluster variant='cards' className='mt-20' />
          </ContainerSection>
        )}

        {isNationalJobTitlePage && Boolean(jobTitlePage?.magazineArticles.length) && (
          <ContainerSection className={clsx('mt-6 px-0', isFiltersSidebarOpen && 'hidden lg:block', slimmed && 'mb-6')}>
            <JobTitlePageMagazineSection articles={jobTitlePage?.magazineArticles ?? []} />
          </ContainerSection>
        )}

        {!isNationalJobTitlePage && isTransparentJobBoard && !isJobTitlesFilterLoading && !pendingRouteDrivenQueryPath && (
          <ContainerSection className={clsx('mt-6 px-0', isFiltersSidebarOpen && 'hidden lg:block', slimmed && 'mb-6')}>
            <MagazinSliderSection
              titleClassName='!text-center text-2xl font-normal lg:mb-16 lg:text-4xl'
              jobPositionJobTitles={jobTitlesForMagazin}
              showFourGrids
            />
          </ContainerSection>
        )}

        {shouldRenderFaqSection && (
          <ContainerSection className='my-20'>
            <QuestionsAndAnswers
              faqs={faqs}
              headingText={tJobBoard(faqHeadingKey, { jobTitle: jobPositionFilterValue, location: locationFilterValue })}
              namespace={namespace}
              translatedJobTitle={jobPositionFilterValue}
              location={locationFilterValue}
              relatedJobTitles={relatedJobTitles}
              relatedLocations={relatedLocations}
              sourceDomainName={sourceDomainName}
              processedSchemaUrls={processedSchemaUrls}
            />
          </ContainerSection>
        )}
      </JobBoardWrapper>
    </>
  );
};

export default JobList;
